import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Tag, ArrowRight } from 'lucide-react';
import { getRecords } from '../../services/recordService';
import { Record } from '../../types/records';
import { format, addDays, isWithinInterval, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';

const HighlightsSection = () => {
    const [events, setEvents] = useState<Record[]>([]);

    useEffect(() => {
        const allRecords = getRecords();
        const today = new Date();
        const nextWeek = addDays(today, 7);

        // Filter events for "This Week"
        const weeklyEvents = allRecords.filter(record => {
            if (!record.startDate) return false;
            const eventDate = new Date(record.startDate);
            return isWithinInterval(eventDate, { start: today, end: nextWeek });
        });

        // Sort by date (nearest first) and take top 8
        const sortedEvents = weeklyEvents.sort((a, b) => {
            return new Date(a.startDate!).getTime() - new Date(b.startDate!).getTime();
        }).slice(0, 8);

        setEvents(sortedEvents);
    }, []);

    if (events.length === 0) {
        return null; // Don't show section if no events
    }

    return (
        <section className="py-16 bg-white dark:bg-zinc-900 border-t border-gray-100 dark:border-zinc-800">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row justify-between items-end mb-10">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Bu hafta kaçırmaman gereken etkinlikler</h2>
                        <p className="mt-2 text-gray-600 dark:text-gray-400">Tarihi yaklaşan ve ilgi gören etkinlikleri senin için öne çıkardık.</p>
                    </div>
                    <Link to="/blog" className="hidden md:flex items-center text-blue-600 dark:text-blue-400 font-semibold hover:text-blue-700 dark:hover:text-blue-300 transition-colors mt-4 md:mt-0">
                        Tümünü Gör <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {events.map((event) => (
                        <Link key={event.id} to={`/blog/${event.id}`} className="group block bg-white dark:bg-card-dark rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-border-dark overflow-hidden h-full flex flex-col">
                            {/* Image */}
                            <div className="relative h-48 overflow-hidden">
                                <img
                                    src={event.coverImage || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=800&q=80'}
                                    alt={event.title}
                                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                />
                                <div className="absolute top-3 right-3">
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-sm ${event.priceType === 'Ücretsiz'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100'
                                        : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100'
                                        }`}>
                                        {event.priceType}
                                    </span>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-5 flex-1 flex flex-col">
                                <div className="flex items-center text-xs text-blue-600 dark:text-blue-400 font-semibold mb-2">
                                    <Tag className="w-3 h-3 mr-1" />
                                    {event.category}
                                </div>

                                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                    {event.title}
                                </h3>

                                <div className="mt-auto space-y-2 text-sm text-gray-500 dark:text-gray-400">
                                    <div className="flex items-center">
                                        <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                                        <span className="line-clamp-1">{event.cityName}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                                        <span>
                                            {event.startDate && format(new Date(event.startDate), 'd MMMM', { locale: tr })}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>

                <div className="mt-8 md:hidden text-center">
                    <Link to="/blog" className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors">
                        Tümünü Gör <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default HighlightsSection;
